import { cn } from '@core/utils';
import { useContext, useRef } from 'react';

import SvgCamera from '../../../../__generated__/icon/SvgCamera';
import { CustomEditorContext } from '..';

type ResultUrl =
  | Promise<string | void | undefined | null>
  | string
  | void
  | undefined
  | null;

export interface ImageButtonProps {
  onClick?: () => ResultUrl;
  limit?: number;
  onLimitExceed?: () => void;
  className?: string;
  iconClassName?: string;
  self?: boolean;
  onChange?: (file?: File) => ResultUrl;
}

/**
 * 에디터 툴바의 이미지 추가 버튼
 */
export const ImageButton = ({
  className,
  iconClassName,
  limit,
  onClick,
  onLimitExceed,
  self,
  onChange,
}: ImageButtonProps) => {
  const inputFileRef = useRef<HTMLInputElement>(null);
  const { editor } = useContext(CustomEditorContext);

  const insertImage = async (url: ResultUrl) => {
    if (!editor)
      throw new Error(
        'ImageButton은 CustomEditorProvider 내부에서만 사용 가능합니다.',
      );

    if (limit) {
      const imageList: string[] = [];

      editor.state.doc.content.forEach((content) => {
        if (content.type.name === 'image') {
          imageList.push(content.attrs.src);
        }
      });

      if (limit <= imageList.length) {
        onLimitExceed?.();

        return;
      }
    }
    if (!url) return;

    const from = editor.state.selection.from;
    const marks = editor.state.doc.resolve(from).marks();

    const currentNode = editor.state.doc.nodeAt(from);
    const href = currentNode?.attrs?.href;

    let chainedCommand = editor
      .chain()
      .focus()
      .insertContent([
        {
          type: 'image',
          attrs: {
            src: url,
            href,
          },
        },
        {
          type: 'paragraph',
          content: '',
        },
      ]);

    marks.forEach((mark) => {
      chainedCommand = chainedCommand.setMark(mark.type, mark.attrs);
    });

    chainedCommand.run();
  };

  return (
    <div className="inline-flex">
      <button
        className={cn(
          'inline-flex size-fit items-center justify-center',
          className,
        )}
        type="button"
        onClick={async (e) => {
          e.preventDefault();

          if (self) {
            inputFileRef.current?.click();

            return;
          }

          const url = await onClick?.();
          insertImage(url);
        }}
      >
        <SvgCamera
          className={cn('size-20 text-Gray-900', iconClassName)}
          name="Camera"
        />
      </button>
      <input
        ref={inputFileRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={async (event) => {
          const file = event.target.files?.[0];
          const url = await onChange?.(file);
          insertImage(url);
        }}
      />
    </div>
  );
};
